import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    CheckCircle2,
    AlertTriangle,
    Send,
    RotateCcw,
    Mail,
    FileText,
    Eye,
    Check,
    X as XIcon,
} from "lucide-react";
import { api, apiErrorMessage, API_BASE } from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/format";

const STATUS_LABEL = {
    not_invited: "Not invited",
    invited: "Invited",
    in_progress: "In progress",
    pending_review: "Pending review",
    complete: "Complete",
};

const STATUS_BADGE = {
    not_invited: "badge-muted",
    invited: "badge-new",
    in_progress: "badge-warning",
    pending_review: "badge-warning",
    complete: "badge-success",
};

export default function AdminOnboarding() {
    const { userId } = useParams();
    return userId ? <DetailView userId={userId} /> : <ListView />;
}

function ListView() {
    const navigate = useNavigate();
    const [users, setUsers] = useState(null);
    const [err, setErr] = useState("");
    const [filter, setFilter] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/admin/onboarding", {
                params: filter ? { status_filter: filter } : {},
            });
            setUsers(data.users);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [filter]);

    const groups = {
        pending_review: users?.filter((u) => u.onboarding_status === "pending_review") || [],
        in_progress: users?.filter((u) => ["invited", "in_progress"].includes(u.onboarding_status)) || [],
        complete: users?.filter((u) => u.onboarding_status === "complete") || [],
        not_invited: users?.filter((u) => u.onboarding_status === "not_invited") || [],
    };

    return (
        <div className="page-shell">
            <header className="fade-up">
                <p className="eyebrow">Onboarding</p>
                <h1 className="h1 mt-3" data-testid="admin-onboarding-title">
                    Onboarding queue
                </h1>
                <p className="text-muted mt-2 max-w-[640px]">
                    Track invitations, review submitted documents, and approve crew before they
                    can be assigned to jobs.
                </p>
            </header>

            <div className="mt-6 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    className={`btn ${filter === "" ? "btn-primary" : "btn-ghost"}`}
                    onClick={() => setFilter("")}
                    data-testid="onb-filter-all"
                >
                    All ({users?.length || 0})
                </button>
                {Object.keys(STATUS_LABEL).map((s) => (
                    <button
                        key={s}
                        type="button"
                        className={`btn ${filter === s ? "btn-primary" : "btn-ghost"}`}
                        onClick={() => setFilter(s)}
                        data-testid={`onb-filter-${s}`}
                    >
                        {STATUS_LABEL[s]}
                    </button>
                ))}
            </div>

            {err && (
                <div
                    className="brand-card mt-6"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                >
                    {err}
                </div>
            )}

            {users === null ? (
                <div className="brand-card mt-6 text-muted">Loading…</div>
            ) : (
                <div className="mt-6 flex flex-col gap-6">
                    {!filter && (
                        <>
                            <Group title="Pending review" highlight users={groups.pending_review} navigate={navigate} testId="onb-group-pending_review" />
                            <Group title="In progress" users={groups.in_progress} navigate={navigate} testId="onb-group-in_progress" />
                            <Group title="Complete" users={groups.complete} navigate={navigate} testId="onb-group-complete" />
                            <Group title="Not invited" users={groups.not_invited} navigate={navigate} testId="onb-group-not_invited" />
                        </>
                    )}
                    {filter && (
                        <Group
                            title={STATUS_LABEL[filter]}
                            users={users}
                            navigate={navigate}
                            testId={`onb-group-${filter}`}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

function Group({ title, users, navigate, highlight, testId }) {
    return (
        <section data-testid={testId}>
            <div className="flex items-baseline justify-between">
                <h2 className="h3">{title}</h2>
                <span className="text-sm text-muted">{users.length}</span>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {users.length === 0 && (
                    <p className="text-muted text-sm">No one in this bucket.</p>
                )}
                {users.map((u) => (
                    <button
                        key={u.id}
                        type="button"
                        className="brand-card text-left fade-up"
                        style={
                            highlight
                                ? { borderColor: "var(--brand-red)" }
                                : {}
                        }
                        onClick={() => navigate(`/admin/onboarding/${u.id}`)}
                        data-testid={`onb-card-${u.id}`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="font-display font-bold">{u.name || u.email}</p>
                                <p className="text-xs text-muted">
                                    {u.email} · <span className="capitalize">{u.role}</span>
                                </p>
                            </div>
                            <span
                                className={`badge-pill ${STATUS_BADGE[u.onboarding_status] || "badge-muted"}`}
                            >
                                {STATUS_LABEL[u.onboarding_status] || u.onboarding_status}
                            </span>
                        </div>
                        <div
                            className="mt-3 h-1.5 rounded-full overflow-hidden"
                            style={{ background: "var(--brand-surface)" }}
                        >
                            <div
                                className="h-full"
                                style={{
                                    width: `${u.summary.percent}%`,
                                    background:
                                        u.summary.percent === 100
                                            ? "var(--brand-success)"
                                            : "var(--brand-red)",
                                }}
                            />
                        </div>
                        <p className="mt-2 text-xs text-muted">
                            {u.summary.percent}% complete · {u.documents.length} doc{u.documents.length === 1 ? "" : "s"} uploaded
                            {u.handbook_acknowledged ? " · handbook ✓" : ""}
                        </p>
                    </button>
                ))}
            </div>
        </section>
    );
}

function DetailView({ userId }) {
    const navigate = useNavigate();
    const [u, setU] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [rejectFor, setRejectFor] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [invited, setInvited] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/onboarding/${userId}`);
            setU(data);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [userId]);

    const invite = async () => {
        setBusy(true);
        setErr("");
        setInvited(false);
        try {
            await api.post(`/admin/onboarding/${userId}/invite`);
            setInvited(true);
            setTimeout(() => setInvited(false), 4000);
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const verify = async (docId) => {
        try {
            await api.post(`/admin/onboarding/${userId}/verify/${docId}`);
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };
    const reject = async (docId, reason) => {
        try {
            await api.post(`/admin/onboarding/${userId}/reject/${docId}`, { reason });
            setRejectFor(null);
            setRejectReason("");
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };
    const approve = async () => {
        setBusy(true);
        setErr("");
        try {
            await api.post(`/admin/onboarding/${userId}/approve`);
            await load();
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    const reset = async () => {
        if (!window.confirm("Reset onboarding for this user? This deletes uploaded documents.")) return;
        setBusy(true);
        try {
            await api.post(`/admin/onboarding/${userId}/reset`);
            navigate("/admin/onboarding");
        } catch (e) {
            setErr(apiErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (!u) {
        return (
            <div className="page-shell">
                {err ? (
                    <div className="brand-card" style={{ borderColor: "var(--brand-red)" }}>{err}</div>
                ) : (
                    <p className="text-muted">Loading…</p>
                )}
            </div>
        );
    }

    const canApprove = u.summary.can_submit_for_review && u.summary.all_documents_verified && u.onboarding_status !== "complete";

    return (
        <div className="page-shell">
            <Link
                to="/admin/onboarding"
                className="link-red inline-flex items-center gap-1 text-sm"
                data-testid="onb-detail-back"
            >
                <ArrowLeft size={14} />
                Back to queue
            </Link>

            <header className="fade-up mt-4 flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="eyebrow">Onboarding · <span className="capitalize">{u.role}</span></p>
                    <h1 className="h1 mt-3" data-testid="onb-detail-name">{u.name || u.email}</h1>
                    <p className="text-muted mt-2 text-sm">
                        {u.email} · {u.phone || "—"}
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                        <span className={`badge-pill ${STATUS_BADGE[u.onboarding_status]}`}>
                            {STATUS_LABEL[u.onboarding_status]}
                        </span>
                        <span className="text-sm text-muted">
                            {u.summary.percent}% complete
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {u.onboarding_status !== "complete" && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={invite}
                            disabled={busy}
                            data-testid="onb-invite"
                        >
                            <Send size={14} />
                            {u.onboarding_status === "not_invited" ? "Send invitation" : "Resend invitation"}
                        </button>
                    )}
                    {canApprove && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={approve}
                            disabled={busy}
                            data-testid="onb-approve"
                        >
                            <CheckCircle2 size={14} />
                            Approve onboarding
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={reset}
                        disabled={busy}
                        data-testid="onb-reset"
                    >
                        <RotateCcw size={14} />
                        Reset
                    </button>
                </div>
            </header>

            {invited && (
                <div
                    className="brand-card mt-4"
                    style={{ background: "var(--brand-cream)", borderColor: "rgba(46,125,50,0.4)" }}
                    data-testid="onb-invite-sent"
                >
                    <p className="eyebrow" style={{ color: "var(--brand-success)" }}>
                        Invitation sent
                    </p>
                    <p className="text-sm mt-1">
                        We emailed an onboarding link to this user. It expires in 7 days.
                    </p>
                </div>
            )}

            {err && (
                <div
                    className="brand-card mt-4"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="onb-detail-error"
                >
                    {err}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
                <div className="lg:col-span-2 flex flex-col gap-5">
                    {/* Documents */}
                    <div className="brand-card fade-up" data-testid="onb-documents-card">
                        <p className="eyebrow">Documents</p>
                        <ul className="mt-3 flex flex-col gap-2">
                            {u.documents.length === 0 && (
                                <p className="text-muted text-sm">Nothing uploaded yet.</p>
                            )}
                            {u.documents.map((d) => (
                                <li
                                    key={d.id}
                                    className="border border-[var(--brand-surface)] rounded-md p-3"
                                    data-testid={`onb-doc-${d.type}`}
                                >
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="min-w-0">
                                            <p className="font-display font-bold capitalize">
                                                {d.type.replace("_", " ")}
                                            </p>
                                            <p className="text-xs text-muted">
                                                <FileText size={12} className="inline" /> {d.filename}{" "}
                                                · {Math.round((d.size || 0) / 1024)} KB · {fmtDateTime(d.uploaded_at)}
                                            </p>
                                            {d.expires_at && (
                                                <p className="text-xs mt-1">
                                                    <span className="badge-pill badge-warning">
                                                        expires {fmtDate(d.expires_at)}
                                                    </span>
                                                </p>
                                            )}
                                            {d.rejection_reason && (
                                                <p
                                                    className="text-xs mt-1 flex items-center gap-1"
                                                    style={{ color: "var(--brand-red)" }}
                                                >
                                                    <AlertTriangle size={12} />
                                                    {d.rejection_reason}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {d.verified && (
                                                <span className="badge-pill badge-success">verified</span>
                                            )}
                                            {d.rejected && (
                                                <span className="badge-pill badge-error">rejected</span>
                                            )}
                                            <a
                                                href={`${API_BASE}/onboarding/documents/${d.id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn btn-ghost"
                                                title="Open document"
                                                data-testid={`onb-doc-view-${d.id}`}
                                            >
                                                <Eye size={14} />
                                            </a>
                                            {!d.verified && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost"
                                                    onClick={() => verify(d.id)}
                                                    title="Mark verified"
                                                    data-testid={`onb-doc-verify-${d.id}`}
                                                >
                                                    <Check
                                                        size={14}
                                                        style={{ color: "var(--brand-success)" }}
                                                    />
                                                </button>
                                            )}
                                            {!d.rejected && (
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost"
                                                    onClick={() => {
                                                        setRejectFor(d.id);
                                                        setRejectReason("");
                                                    }}
                                                    title="Reject"
                                                    data-testid={`onb-doc-reject-${d.id}`}
                                                >
                                                    <XIcon
                                                        size={14}
                                                        style={{ color: "var(--brand-red)" }}
                                                    />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {rejectFor === d.id && (
                                        <form
                                            className="mt-3 flex gap-2 flex-wrap"
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                reject(d.id, rejectReason);
                                            }}
                                        >
                                            <input
                                                className="field-input flex-1 min-w-[200px]"
                                                placeholder="Why is this being rejected?"
                                                value={rejectReason}
                                                onChange={(e) => setRejectReason(e.target.value)}
                                                required
                                                data-testid={`onb-doc-reject-reason-${d.id}`}
                                            />
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                data-testid={`onb-doc-reject-confirm-${d.id}`}
                                            >
                                                Send rejection
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-ghost"
                                                onClick={() => setRejectFor(null)}
                                            >
                                                Cancel
                                            </button>
                                        </form>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Emergency + DD */}
                    <div className="brand-card fade-up delay-1">
                        <p className="eyebrow">Emergency contact</p>
                        {u.emergency_contact ? (
                            <p className="mt-3">
                                <strong>{u.emergency_contact.name}</strong> ({u.emergency_contact.relationship}) ·{" "}
                                {u.emergency_contact.phone}
                            </p>
                        ) : (
                            <p className="text-muted mt-3 text-sm">Not provided.</p>
                        )}
                    </div>

                    {u.role !== "contractor" && (
                        <div className="brand-card fade-up delay-2">
                            <p className="eyebrow">Direct deposit</p>
                            {u.direct_deposit ? (
                                <div className="mt-3 text-sm">
                                    <p>
                                        <strong>{u.direct_deposit.bank_name}</strong> ·{" "}
                                        <span className="capitalize">{u.direct_deposit.account_type}</span>
                                    </p>
                                    <p className="text-muted mt-1">
                                        Routing {u.direct_deposit.routing_number_masked} · Account{" "}
                                        {u.direct_deposit.account_number_masked}
                                    </p>
                                    <p className="text-xs text-muted mt-1">
                                        Authorized {fmtDateTime(u.direct_deposit.authorized_at)}
                                    </p>
                                </div>
                            ) : (
                                <p className="text-muted mt-3 text-sm">Not yet authorized.</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="flex flex-col gap-5">
                    <div className="brand-card fade-up" data-testid="onb-checklist">
                        <p className="eyebrow">Checklist</p>
                        <ul className="mt-3 flex flex-col gap-2 text-sm">
                            {u.summary.steps.map((s) => (
                                <li key={s.key} className="flex items-center gap-2">
                                    {s.complete ? (
                                        <CheckCircle2
                                            size={16}
                                            style={{ color: "var(--brand-success)" }}
                                        />
                                    ) : (
                                        <AlertTriangle
                                            size={16}
                                            style={{ color: "var(--brand-warning)" }}
                                        />
                                    )}
                                    {s.label}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="brand-card fade-up delay-1">
                        <p className="eyebrow">Handbook</p>
                        {u.handbook_acknowledged ? (
                            <p className="mt-3 text-sm">
                                <CheckCircle2
                                    size={14}
                                    style={{ color: "var(--brand-success)" }}
                                    className="inline mr-1"
                                />
                                Acknowledged v{u.handbook_version} ·{" "}
                                {fmtDateTime(u.handbook_acknowledged_at)}
                            </p>
                        ) : (
                            <p className="text-muted mt-3 text-sm">Not yet acknowledged.</p>
                        )}
                    </div>

                    <div className="brand-card fade-up delay-2" style={{ background: "var(--brand-cream)" }}>
                        <p className="eyebrow">Timeline</p>
                        <ul className="mt-3 text-xs space-y-1 text-muted">
                            <li>Invited {u.onboarding_invited_at ? fmtDateTime(u.onboarding_invited_at) : "—"}</li>
                            <li>Completed {u.onboarding_completed_at ? fmtDateTime(u.onboarding_completed_at) : "—"}</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
